import React, { useCallback } from 'react'
import {
  MediaLibraryActions,
  MediaLibraryActionType,
} from '../../../../providers/MLProvider'
import { MediaLibrarySearchFolderResponse } from '../../../../interfaces'

import { Icon } from '@chakra-ui/icons'
import { Flex, HStack } from '@chakra-ui/layout'
import { Tooltip, Text } from '@chakra-ui/react'
import { Base64 } from 'js-base64'
import { FaFolder } from 'react-icons/fa6'
import { useHistory } from 'react-router-dom'
import { useGetQueryParams } from '../../../../hooks/useGetQueryParams'
import { useSingleClickHandling } from '../../../../hooks/useSingleClickHandling'
import { ContextMenuTrigger } from '../../ContextMenu'
import { colors } from '../../../../../ui-kit'

interface FolderElementProps {
  focusedIndex: { index: number; type: string; id: string } | null
  setSelectedAssetMap: { [key: string]: Function }
  selectedAssetMap: { [key: string]: Set<any> }
  setFocusedIndex: Function
  index: number
  getIdMap: { [key: string]: Function }
  dispatch: React.Dispatch<MediaLibraryActionType | MediaLibraryActionType[]>
  mediaCollectionRoute: boolean
  currentMediaCollection: string
  folder: MediaLibrarySearchFolderResponse
  gridTypeForDataset?: 'folder'
  gridOrder: string[]
  assetCountMap: { [key: string]: number }
  route?: string
}

const NewFolderElement: React.FC<FolderElementProps> = React.memo(
  ({
    focusedIndex,
    setSelectedAssetMap,
    selectedAssetMap,
    setFocusedIndex,
    index,
    getIdMap,
    dispatch,
    mediaCollectionRoute,
    currentMediaCollection,
    folder,
    gridTypeForDataset,
    gridOrder,
    assetCountMap,
    route,
  }) => {
    const { name, folderPath } = folder
    const gridType = gridTypeForDataset ?? 'folder'
    const newId = getIdMap[gridType](index)
    const isFocused =
      focusedIndex?.index === index && focusedIndex?.type === gridType
    const isSelected = !!selectedAssetMap[gridType]?.has(newId)
    const history = useHistory()

    const queryFromUrl = useGetQueryParams()

    const singleClickHandler = useSingleClickHandling({
      gridOrder,
      assetCountMap,
      getIdMap,
      selectedAssetMap,
      setSelectedAssetMap,
      focusedIndex,
      setFocusedIndex,
    })

    const handleDoubleClick: React.MouseEventHandler<HTMLDivElement> =
      useCallback(() => {
        dispatch([
          {
            type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
          },
          {
            type: MediaLibraryActions.RESET_FILE_PAGE,
          },
          {
            type: MediaLibraryActions.RESET_FOLDER_PAGE,
          },
        ])
        mediaCollectionRoute
          ? history.push(
              `/media-collections/${currentMediaCollection}/${Base64.encode(
                folderPath,
                true,
              )}${queryFromUrl as string}`,
            )
          : history.push(
              `/media-library/${Base64.encode(folderPath, true)}${
                queryFromUrl as string
              }`,
            )
      }, [
        currentMediaCollection,
        dispatch,
        folderPath,
        history,
        mediaCollectionRoute,
        queryFromUrl,
      ])
    return (
      <ContextMenuTrigger
        onTrigger={event => {
          if (!isSelected) {
            dispatch([
              {
                type: MediaLibraryActions.SELECT_FOLDER,
                payload: {
                  folder,
                  event,
                },
              },
              {
                type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
              },
            ])

            setFocusedIndex({ index: index, type: gridType, id: newId })
          }
        }}
        dispatch={dispatch}
      >
        {contextMenuProps => (
          <Tooltip label={name} placement="top">
            <Flex
              direction="row"
              cursor="pointer"
              flex="1"
              justifyContent="flex-start"
              alignItems="center"
              gap={'7px'}
              padding="12px 16px 12px 14px"
              borderRadius="6px"
              backgroundColor={isSelected ? "#e6ecf8" : '#fff'}
              borderColor={isFocused ? colors.appBlue : colors.appGreyFive}
              borderWidth={'1px'}
              onClick={event => {
                event.stopPropagation()
                event.preventDefault()
                singleClickHandler(gridType, index, newId, event)
                if (route !== '/search') {
                dispatch([
                  {
                    type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
                  },
                ])
              }
              }}
              onDoubleClick={handleDoubleClick}
              {...contextMenuProps}
              aria-label={`${index} folder`}
              data-index={index}
              data-grid-type={gridType}
              className="drag-selectable-element no-right-click-context-menu"
            >
              <Icon as={FaFolder} viewBox={'0 0 16 20'} color={'#a0aec0'} />
              <Text textAlign="left" color={'#0b1b36'} noOfLines={1} 
                  whiteSpace="nowrap">
                {name}
              </Text>
            </Flex>
          </Tooltip>
        )}
      </ContextMenuTrigger>
    )
  },
)
export default NewFolderElement
